/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/** @jsx jsx */
import { jsx } from "@emotion/core"
import { FC, MouseEventHandler, useMemo } from "react"
import { useSelector, useDispatch } from "react-redux"
import { Checkbox, Row, Col } from "@duxton-web/ui"
import { EditableFilled } from "@duxton-web/icons"
import { AppState } from "store/store.type"
import { actionCreator } from "store/store"
import { SubDepartment } from "types/models/menu"
import { useSellingTimes } from "hooks/useSellingTimes"
import { MenuOverviewRow } from "components/common"
import { getSellingTimeByID } from "utils/selling_time_helper"
import {
  getItemWithoutPhotoCount,
  isItemWithoutPhotoMode,
} from "utils/menu_helper"

interface IProps {
  subDepartment: SubDepartment
}

export const MenuSubDepartmentRow: FC<IProps> = ({ subDepartment }: IProps) => {
  const { sellingTimes } = useSellingTimes()
  const {
    drawer: { showItemSelection, itemSelections },
    itemFilter,
  } = useSelector((state: AppState) => state.menu)

  const itemWithoutPhotoCount: number = useMemo(() => {
    // this check to avoid unnecessary looping when all items selected
    if (isItemWithoutPhotoMode(itemFilter)) {
      return getItemWithoutPhotoCount(subDepartment.items)
    }
    return 0
  }, [subDepartment.items, itemFilter])

  const dispatch = useDispatch()

  const disableCheckbox = (subDepartment.items?.length ?? 0) === 0

  const checkList =
    subDepartment.items?.map(
      item => itemSelections?.[item.itemID ?? ""] ?? false,
    ) ?? []

  const checked = !disableCheckbox && checkList.includes(true)

  const indeterminate = !checkList.every((val, i, arr) => val === arr[0])

  const handleCheck = (value: boolean) => {
    dispatch(actionCreator.menu.multiSelectSubDepartment(subDepartment, value))
  }

  const activeSubDepartment = useSelector(
    (state: AppState) => state.menu.overview.subDepartment,
  )
  const isActiveSubDepartment =
    subDepartment.itemClassID === activeSubDepartment?.itemClassID

  const handleClickSelected: MouseEventHandler<HTMLElement> = e => {
    e.stopPropagation()
    if (!showItemSelection) {
      dispatch(
        actionCreator.menu.toggleDrawer({
          showItemSelection: true,
        }),
      )
    }
  }

  const handleClickEdit = () => {
    dispatch(
      actionCreator.menu.toggleDrawer({
        subDepartment,
        showSubDepartment: true,
      }),
    )
  }

  return (
    <MenuOverviewRow
      name={subDepartment.itemClassName ?? ""}
      isActive={isActiveSubDepartment}
      isItemWithoutPhotoMode={isItemWithoutPhotoMode(itemFilter)}
      count={
        isItemWithoutPhotoMode(itemFilter)
          ? itemWithoutPhotoCount
          : subDepartment.totalItems ?? 0
      }
      countNotAvailable={subDepartment.totalUnavailableItems}
      sellingTime={getSellingTimeByID(
        subDepartment.sellingTimeID,
        sellingTimes,
      )}
      renderAfter={
        isItemWithoutPhotoMode(itemFilter) ? (
          false
        ) : (
          <Row
            className={`show-hover-container ${
              showItemSelection ? "show" : ""
            }`}
            wrap={false}
          >
            <Col className={`editBtn ${showItemSelection ? "" : "show"}`}>
              <EditableFilled onClick={handleClickEdit} />
            </Col>
            <Col>
              <Checkbox
                className={`checkbox-container ${
                  showItemSelection ? "show" : ""
                }`}
                disabled={disableCheckbox}
                indeterminate={indeterminate}
                defaultChecked={false}
                checked={checked}
                onChange={(e: any) => handleCheck(e.target.checked)}
                onClick={handleClickSelected}
              />
            </Col>
          </Row>
        )
      }
    />
  )
}
